/* dm2-runtime.js — native behaviours for the EU "Discover Moasure 2 Series" page.
 *
 * Derived from assets/moasure-lx1-runtime.js (same Instant vocabulary, same Swiper-exact geometry rules), with the
 * page-scoped globals renamed and one behaviour added that the LX1 page does not have: scroll-to-section.
 *
 * Like its sibling it STANDS DOWN when instant-core is running (the Instant app embed drives the markup on the live
 * site today) and TAKES OVER when it is not — so the page behaves identically whether or not the app is installed,
 * which is what makes uninstalling Instant a non-event for this page. Never key readiness on window.Instant.*: the
 * app embed sets those flags even when instant-core itself is blocked.
 *
 * The page has 14 Instant roots (one per section); $all(ROOT_SEL) iterates them, and overlay/scroll targets are
 * resolved document-wide so a trigger in one section can drive an overlay in another.
 */
(function () {
  'use strict';
  if (window.__dm2Init) return;
  window.__dm2Init = true;

  // The page is split into six sections, so there are six Instant roots instead of one. Every section carries
  // data-lx1-native, and $all()/takeOver() already iterate, so this one selector covers them all. Overlay
  // targets and scroll triggers are resolved document-wide, which is what lets a trigger in the hero open an
  // overlay that lives in the overlays section.
  var ROOT_SEL = '[data-dm2-native]';
  var DISABLED = 'instant-slider-button-disabled';
  var LOCK = 'instant-slider-button-lock';

  function $all(sel, ctx) { try { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); } catch (e) { return []; } }
  function safe(fn) { return function () { try { return fn.apply(this, arguments); } catch (e) { /* swallow */ } }; }
  function closest(el, sel) { while (el && el.nodeType === 1) { if (el.matches && el.matches(sel)) return el; el = el.parentElement; } return null; }
  function bound(el, key) { key = key || '__dm2Bound'; if (!el || el[key]) return true; el[key] = true; return false; }
  function kids(el, cls) {
    var out = [];
    if (!el) return out;
    for (var i = 0; i < el.children.length; i++) if (!cls || el.children[i].classList.contains(cls)) out.push(el.children[i]);
    return out;
  }

  /* ================= sliders ================= */
  var mains = {};    // sliderId -> api (data-instant-type="slider")
  var thumbs = {};   // sliderId -> api (data-instant-type="thumbnails")

  function readParams(el) {
    try { var s = document.getElementById('instant-slider-' + el.id + '-params'); return s ? JSON.parse(s.textContent) : null; } catch (e) { return null; }
  }
  function breakpoint(params) {
    if (!params || !params.breakpoints) return {};
    var w = window.innerWidth, best = null, bestK = -1;
    for (var k in params.breakpoints) {
      var n = parseInt(k, 10);
      if (w >= n && n > bestK) { bestK = n; best = params.breakpoints[k]; }
    }
    return best || {};
  }

  function initSlider(el) {
    if (bound(el)) return null;
    var wrapper = el.querySelector('.instant-slider-wrapper');
    if (!wrapper) return null;
    var slides = kids(wrapper, 'instant-slider-slide');
    if (!slides.length) return null;

    var params = readParams(el) || {};
    var container = closest(el, '.instant-slider-container') || el.parentElement || document;
    var sid = el.getAttribute('data-instant-slider-id');
    var isThumbs = el.getAttribute('data-instant-type') === 'thumbnails';

    var idx = 0, slideW = 0, gap = 0, spv = 1, snaps = 1, maxIdx = 0, centered = false;
    var pagEl = null, bulletClass = '', bullets = [];

    el.classList.add('instant-slider-initialized', 'instant-slider-horizontal', 'instant-slider-backface-hidden');
    if (params.watchSlidesProgress) el.classList.add('instant-slider-watch-progress');
    el.setAttribute('data-instant-slider-initialized', 'true');

    function navEls(sel) {
      if (!sel) return [];
      var els = $all(sel, container);
      return els.length ? els : $all(sel, document);
    }
    function offset(i) { return i * (slideW + gap); }

    function layout() {
      var b = breakpoint(params);
      spv = b.slidesPerView || 1;
      gap = b.spaceBetween || 0;
      centered = !!b.centeredSlides;
      var W = el.clientWidth;
      slideW = spv === 1 ? W : (W - (spv - 1) * gap) / spv;
      for (var i = 0; i < slides.length; i++) {
        slides[i].style.width = slideW + 'px';
        slides[i].style.marginRight = gap ? gap + 'px' : '';
      }
      var next = centered ? slides.length : Math.max(1, slides.length - spv + 1);
      var changed = next !== snaps;
      snaps = next; maxIdx = snaps - 1;
      if (changed || !bullets.length) renderBullets();
    }

    function updateNav() {
      var nav = params.navigation;
      if (!nav) return;
      // watchOverflow: when every slide already fits there is nothing to navigate, and Swiper LOCKS the buttons —
      // adds instant-slider-button-lock, which the stylesheet hides with display:none. Two sliders on this page are
      // in that state at >=1025px (the PRO compare card has 2 slides, the technique overlay 3), so without this the
      // native runtime leaves two pairs of arrows visible that the live page hides.
      var locked = params.watchOverflow !== false && maxIdx <= 0;
      navEls(nav.prevEl).forEach(function (b) { b.classList.toggle(LOCK, locked); b.classList.toggle(DISABLED, locked || idx <= 0); });
      navEls(nav.nextEl).forEach(function (b) { b.classList.toggle(LOCK, locked); b.classList.toggle(DISABLED, locked || idx >= maxIdx); });
    }
    function updateBullets() {
      for (var i = 0; i < bullets.length; i++) {
        bullets[i].classList.toggle('instant-slider-pagination-bullet-active', i === idx);
        bullets[i].classList.toggle('instant-slider-pagination-bullet-active-main', i === idx);
      }
    }
    function renderBullets() {
      var p = params.pagination;
      if (!p || !p.el) return;
      if (!pagEl) {
        pagEl = container.querySelector(p.el) || document.querySelector(p.el);
        if (!pagEl) return;
        var first = pagEl.firstElementChild;
        if (first) {
          bulletClass = first.className.split(/\s+/).filter(function (c) {
            return c && c.indexOf('instant-slider-pagination-bullet') !== 0;
          }).join(' ');
        }
        pagEl.classList.add('instant-slider-pagination-clickable', 'instant-slider-pagination-bullets', 'instant-slider-pagination-horizontal');
      }
      pagEl.innerHTML = '';
      bullets = [];
      for (var i = 0; i < snaps; i++) {
        var b = document.createElement('span');
        b.className = 'instant-slider-pagination-bullet' + (bulletClass ? ' ' + bulletClass : '');
        b.addEventListener('click', safe((function (k) { return function () { go(k, true); }; })(i)));
        pagEl.appendChild(b);
        bullets.push(b);
      }
      updateBullets();
    }

    function go(i, animate) {
      i = Math.max(0, Math.min(i, maxIdx));
      idx = i;
      wrapper.style.transitionDuration = animate === false ? '0ms' : '300ms';
      wrapper.style.transform = 'translate3d(-' + offset(i) + 'px, 0px, 0px)';
      for (var n = 0; n < slides.length; n++) {
        slides[n].classList.toggle('instant-slider-slide-active', n === i);
        slides[n].classList.toggle('instant-slider-slide-prev', n === i - 1);
        slides[n].classList.toggle('instant-slider-slide-next', n === i + 1);
      }
      updateNav(); updateBullets();
      if (!isThumbs && sid && thumbs[sid]) thumbs[sid].follow(i);
    }

    // thumbnails: the thumb at the MAIN slider's index carries `instant-slider-thumb-active` (the class the CSS lifts).
    function follow(mainIdx) {
      for (var n = 0; n < slides.length; n++) slides[n].classList.toggle('instant-slider-thumb-active', n === mainIdx);
      if (mainIdx < idx || mainIdx > idx + spv - 1) go(Math.min(mainIdx, Math.max(0, slides.length - spv)), true);
    }

    var nav = params.navigation;
    if (nav) {
      navEls(nav.nextEl).forEach(function (b) { if (!bound(b, '__dm2Nav')) b.addEventListener('click', safe(function (e) { e.preventDefault(); go(idx + 1, true); })); });
      navEls(nav.prevEl).forEach(function (b) { if (!bound(b, '__dm2Nav')) b.addEventListener('click', safe(function (e) { e.preventDefault(); go(idx - 1, true); })); });
    }

    /* drag / swipe */
    var pid = null, startX = 0, startT = 0, base = 0, dragging = false, downTarget = null, taps = [];
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    el.addEventListener('pointerdown', safe(function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pid = e.pointerId; startX = e.clientX; startT = Date.now(); base = -offset(idx); dragging = false; downTarget = e.target;
      // NB: do NOT preventDefault() here — on pointerdown that suppresses the compatibility click event, which
      // would kill clicks on children inside the slider (the thumbnails). Native image-drag is stopped by the
      // dragstart handler above, and text selection by user-select while dragging.
      try { el.setPointerCapture(pid); } catch (err) {}
    }));
    el.addEventListener('pointermove', safe(function (e) {
      if (pid === null || e.pointerId !== pid) return;
      var dx = e.clientX - startX;
      if (!dragging && Math.abs(dx) <= 5) return;
      dragging = true;
      wrapper.style.transitionDuration = '0ms';
      wrapper.style.userSelect = 'none';
      var pos = base + dx, min = -offset(maxIdx), max = 0;
      if (pos > max) pos = max + (pos - max) * 0.3;
      if (pos < min) pos = min + (pos - min) * 0.3;
      wrapper.style.transform = 'translate3d(' + pos + 'px, 0px, 0px)';
    }));
    function endDrag(e) {
      if (pid === null || (e && e.pointerId !== undefined && e.pointerId !== pid)) return;
      try { el.releasePointerCapture(pid); } catch (err) {}
      pid = null;
      wrapper.style.userSelect = '';
      if (!dragging) {
        if (taps.length && downTarget) {
          var slide = closest(downTarget, '.instant-slider-slide');
          var n = slide ? slides.indexOf(slide) : -1;
          if (n >= 0) taps.forEach(function (fn) { safe(fn)(n); });
        }
        return;
      }
      dragging = false;
      var dx = (e && e.clientX !== undefined ? e.clientX : startX) - startX;
      var dt = Math.max(1, Date.now() - startT);
      if (Math.abs(dx) > slideW * 0.2 || Math.abs(dx) / dt > 0.3) go(idx - (dx > 0 ? 1 : -1), true); else go(idx, true);
      el.__lx1SuppressClick = true;
      setTimeout(function () { el.__lx1SuppressClick = false; }, 300);
    }
    el.addEventListener('pointerup', safe(endDrag));
    el.addEventListener('pointercancel', safe(function () { if (pid !== null) { try { el.releasePointerCapture(pid); } catch (err) {} pid = null; dragging = false; wrapper.style.userSelect = ''; go(idx, true); } }));
    el.addEventListener('click', function (e) { if (el.__lx1SuppressClick) { e.preventDefault(); e.stopPropagation(); } }, true);

    var api = { el: el, go: go, layout: layout, follow: follow, index: function () { return idx; }, spv: function () { return spv; }, onTap: function (fn) { taps.push(fn); } };
    if (sid) (isThumbs ? thumbs : mains)[sid] = api;

    layout();
    go(0, false);
    return api;
  }

  function initSliders(root) {
    $all('[data-instant-type="slider"]', root).forEach(function (el) { safe(initSlider)(el); });
    $all('[data-instant-type="thumbnails"]', root).forEach(function (el) {
      var api = safe(initSlider)(el);
      var sid = el.getAttribute('data-instant-slider-id');
      var main = sid && mains[sid];
      if (!api || !main) return;
      api.onTap(function (n) { main.go(n, true); });
      api.follow(main.index());
    });
  }

  /* ================= accordions ================= */
  function contentOf(item) { return item.querySelector('[data-instant-type="accordion-content"]'); }
  function setAcc(item, open) {
    var c = contentOf(item);
    if (c) {
      c.style.setProperty('--instant-accordion-content-width', c.clientWidth + 'px');
      c.style.setProperty('--instant-accordion-content-height', open ? c.scrollHeight + 'px' : '0px');
    }
    item.setAttribute('data-state', open ? 'open' : 'closed');
  }
  function initAccordions(root) {
    $all('[data-instant-type="accordion-container"]', root).forEach(function (container) {
      if (bound(container, '__dm2Acc')) return;
      var items = $all('[data-instant-type="accordion-item"]', container).filter(function (i) { return closest(i, '[data-instant-type="accordion-container"]') === container; });
      var multi = container.getAttribute('data-is-multi-open-enabled') === 'true';
      items.forEach(function (item) {
        var c = contentOf(item);
        if (c) { c.style.setProperty('--instant-accordion-content-height', '0'); c.style.setProperty('--instant-accordion-content-width', c.clientWidth + 'px'); }
        if (item.getAttribute('data-state') !== 'open') item.setAttribute('data-state', 'closed');
        var header = item.querySelector('[data-instant-type="accordion-header"]');
        if (!header || bound(header, '__dm2AccH')) return;
        header.addEventListener('click', safe(function (e) {
          e.preventDefault(); e.stopPropagation();
          var open = item.getAttribute('data-state') !== 'open';
          if (open && !multi) items.forEach(function (o) { if (o !== item) setAcc(o, false); });
          setAcc(item, open);
        }));
      });
      container.__dm2AccItems = items;
    });
  }

  /* ================= overlays ================= */
  var savedOverflow = null;
  function overlayFor(id) { return document.getElementById('i' + id) || document.getElementById(id); }
  function openOverlay(o) {
    if (!o || o.getAttribute('data-state') === 'open') return;
    if (savedOverflow === null) savedOverflow = document.body.style.overflow;
    o.setAttribute('data-state', 'open');
    document.body.style.overflow = 'hidden';
    var f = o.querySelector('button');
    if (f) { try { f.focus({ preventScroll: true }); } catch (e) {} }
  }
  function closeOverlay(o) {
    if (!o) return;
    o.setAttribute('data-state', 'closed');
    $all('video', o).forEach(function (v) { try { v.pause(); } catch (e) {} });
    if (!document.querySelector('[data-instant-type="overlay"][data-state="open"]')) {
      document.body.style.overflow = savedOverflow || '';
      savedOverflow = null;
    }
  }
  function initOverlays(root) {
    $all('[data-instant-action-type="open-overlay"]', root).forEach(function (btn) {
      if (bound(btn, '__dm2Ov')) return;
      btn.addEventListener('click', safe(function (e) {
        // every trigger is a <button> with no type inside the root <form> — without this it submits and reloads
        e.preventDefault(); e.stopPropagation();
        var id = btn.getAttribute('data-instant-action-id');
        if (!id) { closeOverlay(closest(btn, '[data-instant-type="overlay"]')); return; }
        var o = overlayFor(id);
        if (!o) return;
        if (closest(btn, '[data-instant-type="overlay"]') === o && o.getAttribute('data-state') === 'open') closeOverlay(o);
        else openOverlay(o);
      }));
    });
    $all('[data-instant-type="overlay"]', root).forEach(function (o) {
      if (bound(o, '__dm2OvRoot')) return;
      o.addEventListener('pointerdown', safe(function (e) { if (e.target === o) closeOverlay(o); }));
    });
    if (!window.__dm2Esc) {
      window.__dm2Esc = true;
      document.addEventListener('keydown', safe(function (e) {
        if (e.key !== 'Escape') return;
        var open = $all('[data-instant-type="overlay"][data-state="open"]');
        if (open.length) closeOverlay(open[open.length - 1]);
      }));
    }
  }

  /* ================= scroll triggers ================= */
  // scroll-to-section — Instant's <a data-instant-action-type="scroll-to-section" data-instant-action-id="HASH">.
  // The anchor also carries href="#iHASH", so without preventDefault the browser would jump instantly and the
  // smooth scroll would never be seen. Targets are resolved document-wide (the target usually lives in another
  // section now).
  function initScrollTo(root) {
    $all('[data-instant-action-type="scroll-to-section"]', root).forEach(function (el) {
      if (bound(el, '__dm2ScrollTo')) return;
      el.addEventListener('click', safe(function (e) {
        var id = el.getAttribute('data-instant-action-id');
        if (!id) return;
        var t = document.getElementById('i' + id) || document.getElementById(id);
        if (!t) return;
        e.preventDefault();
        var y = t.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0);
        try { window.scrollTo({ top: y, behavior: 'smooth' }); } catch (err) { window.scrollTo(0, y); }
      }));
    });
  }

  function initScrollTriggers(root) {
    var els = $all('.instant-scroll-trigger', root).filter(function (el) { return !bound(el, '__dm2Scroll'); });
    if (!els.length) return;
    var ticking = false;
    function evaluate() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      els.forEach(function (el) {
        var offset = parseInt(el.getAttribute('data-instant-scroll-into-view-offset') || '0', 10) || 0;
        var replay = el.getAttribute('data-instant-scroll-into-view-replay') === 'true';
        var shown = y >= offset;
        if (!shown && !replay && el.__dm2Shown) return;
        if (shown) el.__dm2Shown = true;
        el.classList.toggle('instant-scroll-trigger--hidden', !shown);
      });
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; window.requestAnimationFrame(evaluate); } }, { passive: true });
    evaluate();
  }

  /* ================= tabs ================= */
  function initTabs(root) {
    $all('[data-instant-type="tabs-container"]', root).forEach(function (container) {
      if (bound(container, '__lx1Tabs')) return;
      var content = $all('[data-instant-type="tabs-content"]', container).filter(function (c) { return closest(c, '[data-instant-type="tabs-container"]') === container; })[0];
      var triggers = $all('[data-instant-type="tabs-trigger"]', container).filter(function (t) { return closest(t, '[data-instant-type="tabs-container"]') === container; });
      if (!content || !triggers.length) return;
      var panes = kids(content).filter(function (p) { return p.getAttribute('data-instant-type') === 'tabs-pane'; });
      function activate(tabId) {
        triggers.forEach(function (t) { t.setAttribute('data-instant-state', t.getAttribute('data-instant-tab-id') === tabId ? 'active' : 'inactive'); });
        panes.forEach(function (p) {
          var on = p.id === tabId;
          p.setAttribute('data-instant-state', on ? 'open' : 'closed');
          p.style.display = on ? '' : 'none';
        });
      }
      triggers.forEach(function (t) {
        if (bound(t, '__lx1Tab')) return;
        t.addEventListener('click', safe(function (e) { e.preventDefault(); e.stopPropagation(); activate(t.getAttribute('data-instant-tab-id')); }));
      });
      var active = triggers.filter(function (t) { return t.getAttribute('data-instant-state') === 'active'; })[0] || triggers[0];
      activate(active.getAttribute('data-instant-tab-id'));
    });
  }

  /* ================= resize ================= */
  function initResize() {
    if (window.__lx1Resize) return;
    window.__lx1Resize = true;
    var t = null;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(safe(function () {
        var all = [];
        for (var k in mains) all.push(mains[k]);
        for (var k2 in thumbs) all.push(thumbs[k2]);
        all.forEach(function (s) { s.layout(); s.go(Math.min(s.index(), 1e9), false); });
        for (var k3 in mains) if (thumbs[k3]) thumbs[k3].follow(mains[k3].index());
        $all('[data-instant-type="accordion-item"][data-state="open"]').forEach(function (item) { setAcc(item, true); });
      }), 100);
    });
  }

  /* ================= mode decision ================= */
  // Evidence that instant-core ITSELF ran (never window.Instant.initialized — the app embed sets that inline).
  function coreRan() {
    var I = window.Instant;
    if (I && (typeof I.initSwiper === 'function' || I.initializedListeners || I.eventAbortController || I.api)) return true;
    return !!document.querySelector('[data-instant-slider-initialized="true"]');
  }
  function standDown() { window.__dm2Mode = 'instant'; }
  function takeOver() {
    window.__dm2Mode = 'native';
    $all(ROOT_SEL).forEach(function (root) {
      safe(initSliders)(root);
      safe(initAccordions)(root);
      safe(initOverlays)(root);
      safe(initTabs)(root);
      safe(initScrollTriggers)(root);
      safe(initScrollTo)(root);
    });
    safe(initResize)();
    window.__dm2nativeReady = true;
    try { window.dispatchEvent(new Event('dm2:ready')); } catch (e) {}
  }
  function decide() {
    if (coreRan()) return standDown();
    if (!document.querySelector('script[src*="instant-core"]')) return takeOver();
    var tries = 0;
    (function poll() {
      if (coreRan()) return standDown();
      if (++tries > 15) return takeOver();   // ~1.5s: the tag is present but blocked/failed
      setTimeout(poll, 100);
    })();
  }

  // This tag is `defer` and sits after the app embed's instant-core tag, so by now instant-core has run if it loaded.
  decide();
})();
